/**
 * pushNotifications.js — device registration for FCM.
 *
 * On Capacitor native (iOS / Android): uses @capacitor/push-notifications
 * to request permission, get the FCM token, and register listeners for
 * incoming messages.
 *
 * On the web: uses the Firebase JS messaging API to request Notification
 * permission + fetch an FCM registration token, using the same Firebase
 * config that powers Auth.
 *
 * Both paths POST the token to /api/notifications/register-device so the
 * backend can send pushes later. Safe no-op when Firebase isn't configured.
 */
import { api } from "./api";
import { ensureFirebaseReady, isFirebaseConfigured } from "./firebase";

function isNativeCapacitor() {
  return typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
}

async function registerToken(token, platform) {
  if (!token) return;
  try {
    await api.post("/notifications/register-device", {
      fcm_token: token, platform,
      device_id: null,
      app_version: process.env.REACT_APP_VERSION || null,
    });
  } catch (e) {
    // Silent — token can be re-registered later; not fatal.
    console.warn("[push] register-device failed", e);
  }
}

/**
 * Kick off push registration for the current session. Idempotent — safe to
 * call on every app boot. Returns a status string used for debug/logs.
 */
export async function initPushNotifications() {
  if (!(await ensureFirebaseReady())) return "not_configured";

  if (isNativeCapacitor()) {
    try {
      const mod = await import("@capacitor/push-notifications");
      const PushNotifications = mod.PushNotifications;
      // Attach listeners BEFORE calling register() — Capacitor's plugin
      // uses notifyListeners() which drops events if no listener is
      // subscribed yet. Registering listeners first avoids a race where
      // the FCM token arrives before our subscription lands.
      let gotToken = false;
      PushNotifications.addListener("registration", (t) => {
        gotToken = true;
        const val = t?.value || "";
        console.log(`[push] iOS registration event fired — token=${val.slice(0, 24)}… (${val.length} chars)`);
        // FCM tokens are ~152+ chars; APNs hex tokens are 64 chars.
        // If we get 64 chars something upstream is still sending APNs.
        if (val.length > 0 && val.length < 100) {
          console.warn("[push] Token looks like APNs hex (short) — AppDelegate FCM bridge may not be active. Check Xcode console for [v92 FCM] logs.");
        }
        registerToken(val, isIos() ? "ios" : "android");
      });
      PushNotifications.addListener("registrationError", (e) => {
        console.error("[push] native registration error", e);
      });
      // v92.5 — Foreground notification receive event. The Capacitor plugin
      // fires this via its UNUserNotificationCenterDelegate.willPresent
      // callback (iOS) / MessagingService.onMessageReceived (Android). We
      // rely on the native plugin's `presentationOptions` config (set in
      // capacitor.config.ts) to actually show the OS banner while the app
      // is in the foreground — this listener is for logging + optional
      // custom in-app UI (e.g. a badge counter refresh).
      PushNotifications.addListener("pushNotificationReceived", (notif) => {
        console.log("[push] foreground received:",
          { title: notif?.title, body: notif?.body, data: notif?.data });
        try {
          // Notify any subscribed screens (e.g. the bell/messages) so they
          // can refresh their unread count in real time.
          window.dispatchEvent(new CustomEvent("push:received", { detail: notif }));
        } catch { /* CustomEvent may not exist in some polyfilled envs */ }
      });
      // v92.5 — Tap handler. Fires on tap in ALL app states:
      //   * Foreground: user taps the in-app banner.
      //   * Background: user taps the tray notification.
      //   * Terminated: iOS/Android launches the app; the plugin buffers
      //     this event (retainUntilConsumed:true on iOS) until the JS
      //     listener subscribes on next boot.
      // We route by reading data.deep_link / data.click_action / data.url
      // from the notification payload — the backend puts these values in
      // messaging.Message(data={"deep_link": "/messages/chat_abc"}, ...).
      PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
        const notif = action?.notification || {};
        const data = notif.data || {};
        // v149.50 — HQ branch invitations open the Accept / Decline screen.
        const target = data.kind === "org_branch_invite"
          ? "/app?branch_invite=1"
          : (data.deep_link || data.click_action || data.url || null);
        console.log("[push] notification tapped —", { data, target });
        if (!target) return;
        try {
          let path = null;
          if (target.startsWith("/")) {
            path = target;
          } else if (typeof window !== "undefined" &&
                     target.startsWith(window.location.origin + "/")) {
            path = target.slice(window.location.origin.length);
          } else {
            console.warn("[push] ignoring off-origin tap target:", target);
            return;
          }
          // v147.37 — Apply the same prefixing rules the in-app
          // NotificationsBell uses so the native tap handler routes to
          // the correct authenticated shell even when the backend
          // notification stored a legacy path missing `/app/`. Without
          // this, a stored `/action-hub/analytics?tab=reviews` opened a
          // blank page because React Router doesn't have a bare
          // `/action-hub/...` route. Keep in sync with
          // components/NotificationsBell.jsx TOP_LEVEL_ROUTES + rules.
          const TOP_LEVEL_ROUTES = ["/qr-sales", "/store", "/onboarding"];
          const publicMatch = path.match(/^\/(u|p)\/([^/?#]+)/);
          if (publicMatch) {
            const rest = path.slice(publicMatch[0].length);
            path = `/app/view/${publicMatch[1]}/${publicMatch[2]}${rest}`;
          } else if (
            TOP_LEVEL_ROUTES.some(
              (prefix) =>
                path === prefix
                || path.startsWith(prefix + "/")
                || path.startsWith(prefix + "?")
            )
          ) {
            // Top-level route — leave as-is, no `/app` prefix.
          } else if (!path.startsWith("/app") && !path.startsWith("/admin")) {
            path = "/app" + (path.startsWith("/") ? path : "/" + path);
          }
          // SPA navigation — pushState + popstate fires react-router.
          window.history.pushState({}, "", path);
          window.dispatchEvent(new PopStateEvent("popstate"));
        } catch (e) {
          console.error("[push] failed to route tap", e);
        }
      });
      const permission = await PushNotifications.requestPermissions();
      if (permission.receive !== "granted") return "permission_denied";
      await PushNotifications.register();
      // Diagnostic warning if no registration event within 15 seconds.
      // Almost always means: APNs Auth Key misconfigured in Firebase, or
      // aps-environment doesn't match the provisioning profile, or the
      // AppDelegate FCM bridge patch didn't apply.
      setTimeout(() => {
        if (!gotToken) {
          console.warn("[push] No registration event 15s after register() — check Xcode console for [v92 FCM] logs. Common causes: (1) APNs Auth Key not uploaded/wrong Team ID in Firebase Console → Cloud Messaging, (2) aps-environment='development' but signed with distribution profile (or vice versa), (3) Bundle ID mismatch between app and Firebase iOS app registration.");
        }
      }, 15000);
      return "native_registered";
    } catch (e) {
      console.warn("[push] native init failed", e);
      return "native_error";
    }
  }

  // Web push via Firebase messaging
  if (!isFirebaseConfigured()) return "not_configured";
  if (typeof Notification === "undefined") return "notifications_unsupported";
  try {
    let permission = Notification.permission;
    if (permission === "default") {
      permission = await Notification.requestPermission();
    }
    if (permission !== "granted") return "permission_denied";

    // Fetch the public config so we always have the freshest VAPID key. If
    // Firebase env vars are populated locally we still call this to grab
    // the vapidKey (which lives only in the DB / Super Admin UI).
    let cfg = null;
    try {
      cfg = await (await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/firebase/public-config`,
      )).json();
    } catch { /* fall through — env may provide everything */ }

    const { getApp, initializeApp, getApps } = await import("firebase/app");
    let app;
    try { app = getApp(); } catch { app = null; }
    if (!app) {
      if (!cfg?.configured) return "not_configured";
      app = getApps().length ? getApps()[0] : initializeApp({
        apiKey: cfg.apiKey, authDomain: cfg.authDomain, projectId: cfg.projectId,
        appId: cfg.appId, messagingSenderId: cfg.messagingSenderId,
        storageBucket: cfg.storageBucket,
      });
    }

    // Register our FCM service worker (required for background pushes).
    let swRegistration;
    if ("serviceWorker" in navigator) {
      try {
        swRegistration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js", { scope: "/firebase-cloud-messaging-push-scope" },
        );
      } catch (e) {
        console.warn("[push] SW register failed", e);
      }
    }

    const { getMessaging, getToken, onMessage, isSupported } =
      await import("firebase/messaging");
    if (!(await isSupported())) return "web_push_unsupported";
    const messaging = getMessaging(app);
    // vapidKey precedence: DB → env → undefined (SDK default).
    const vapidKey =
      (cfg?.vapidKey && cfg.vapidKey.trim()) ||
      process.env.REACT_APP_FIREBASE_VAPID_KEY ||
      undefined;
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swRegistration,
    });
    if (!token) return "no_token";
    await registerToken(token, "web");
    onMessage(messaging, (message) => {
      // Foreground toast — background pushes are handled by the browser.
      const { title, body } = message.notification || {};
      if (title && typeof window !== "undefined") {
        import("sonner").then(({ toast }) => toast(title, { description: body }));
      }
    });
    return "web_registered";
  } catch (e) {
    console.warn("[push] web init failed", e);
    return "web_error";
  }
}

function isIos() {
  return typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);
}
